/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  ApplicationRef,
  ChangeDetectorRef,
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { V3FolioMasonryCardComponent } from '@app/app/page-modules/folio/components/view/shared/masonry-section/v3-folio-masonry-card/v3-folio-masonry-card.component';
import { VersionHelper } from '@app/app/shared/helpers/version.helper';
import { FolioMasonryCardComponent } from '../../page-modules/folio/components/view/shared/masonry-section/folio-masonry-card/folio-masonry-card.component';
import { MomentCard } from '../../page-modules/folio/models';
/* eslint-disable @typescript-eslint/no-explicit-any */

declare const Bricklayer;

export interface IMasonryCardBase {
  card: MomentCard;

  archiveMoment: boolean;

  removeCard: EventEmitter<string>;

  likeMoment?: EventEmitter<string>;

  unlikeMoment?: EventEmitter<string>;

  saveMoment?: EventEmitter<string>;

  unsaveMoment?: EventEmitter<string>;

  uid: string;

  editMode: boolean;

  isFolioSection: boolean;

  cardVariant?: 'default' | 'community';
}

@Directive({
  selector: '[ogMasonry]',
})
export class MasonryDirective implements OnChanges {
  /** Card data. */
  @Input() cards: MomentCard[] = [];
  @Input() removedItemsCount: number;
  @Input() archiveMoment: boolean;
  @Input() shouldAnimateOnLoadMore: boolean;
  @Input() disabled = false;
  @Input() uid: string;
  @Input() editMode = false;
  @Input() isFolioSection = false;
  @Input() cardVariant: 'default' | 'community' = 'default';

  @Output() removeCard = new EventEmitter<string>();
  @Output() likeMoment = new EventEmitter<string>();
  @Output() unlikeMoment = new EventEmitter<string>();
  @Output() saveMoment = new EventEmitter<string>();
  @Output() unsaveMoment = new EventEmitter<string>();

  isV3Enabled = VersionHelper.isV3Enabled();

  private brickLayer: any;
  private cardComponents: ComponentRef<any>[] = [];

  constructor(
    private el: ElementRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private applicationRef: ApplicationRef,
    private injector: Injector,
    private cdr: ChangeDetectorRef,
  ) {}

  private initBrick() {
    this.brickLayer = new Bricklayer(this.el.nativeElement);
    this.cardComponents = [];
    this.appendCards(this.cards, false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.brickLayer && this.cards) {
      // using timeout so the elements are placed in the DOM first during route changes
      setTimeout(() => this.initBrick());
    }

    if (!changes.cards.firstChange) {
      // If new card added but length the same
      if (changes.cards.previousValue.length === changes.cards.currentValue.length && changes.cards.previousValue.length > 0) {
        const hasAnyCardChanged = changes.cards.previousValue.some(
          (prevCard, index) => !this.areCardsEqual(prevCard, changes.cards.currentValue[index]),
        );

        if (hasAnyCardChanged) {
          this.updateExistingCards(changes.cards.currentValue);
        }
      } else if (changes.cards.currentValue.length > changes.cards.previousValue.length) {
        // if cards were added, append to masonry
        const newMomentsToAppend = changes.cards.currentValue.filter(
          (card) => !changes.cards.previousValue.map((c) => c._id).includes(card._id),
        );
        this.appendCards(newMomentsToAppend, this.shouldAnimateOnLoadMore);
        // if cards were removed, remove from masonry
      } else {
        if (this.removedItemsCount && this.removedItemsCount === 1) {
          if (changes.cards.previousValue.length > changes.cards.currentValue.length) {
            const momentsToRemove = changes.cards.previousValue.filter(
              (card) => !changes.cards.currentValue.map((c) => c._id).includes(card._id),
            );

            momentsToRemove.forEach((moment) => this.removeSingleMoment(changes.cards.previousValue, moment));
          }
        } else {
          if (changes.cards.previousValue.length >= changes.cards.currentValue.length) {
            this.removeBulkMoments(changes.cards.previousValue);
          }
        }
      }
    }
  }

  appendCards(cards: MomentCard[], animation: boolean) {
    cards.forEach((card, index) => {
      const momentCardHTML = this.getMomentCardHTML(card);

      if (animation) {
        setTimeout(() => this.brickLayer.append(momentCardHTML), index * 100);
      } else {
        this.brickLayer.append(momentCardHTML);
      }
    });
  }

  private areCardsEqual(card1: MomentCard, card2: MomentCard): boolean {
    return (
      card1?._id === card2?._id &&
      card1?.likes?.totalLikes === card2?.likes?.totalLikes &&
      card1?.likes?.liked === card2?.likes?.liked &&
      card1?.saved === card2?.saved &&
      card1?.visible === card2?.visible
    );
  }

  private getMomentCardHTML(momentData: MomentCard): HTMLElement {
    // select text or normal moment component
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    let component: any;

    if (this.isV3Enabled) {
      component = V3FolioMasonryCardComponent;
    } else {
      component = FolioMasonryCardComponent;
    }

    // create a moment card component reference
    const momentCardRef: ComponentRef<any> = this.componentFactoryResolver.resolveComponentFactory(component as any).create(this.injector);

    // attach component to the appRef so that it will be dirty checked
    this.applicationRef.attachView(momentCardRef.hostView);

    // we need to get access to Input() of injected component data
    const instance = momentCardRef.instance as IMasonryCardBase;
    instance.card = momentData;
    instance.card.isDisabled = this.disabled;
    instance.archiveMoment = this.archiveMoment;
    instance.removeCard = this.removeCard;
    instance.likeMoment = this.likeMoment;
    instance.unlikeMoment = this.unlikeMoment;
    instance.saveMoment = this.saveMoment;
    instance.unsaveMoment = this.unsaveMoment;
    instance.uid = this.uid;
    instance.editMode = this.editMode;
    instance.isFolioSection = this.isFolioSection;
    instance.cardVariant = this.cardVariant;

    this.cardComponents.push(momentCardRef);

    // return DOM element from component
    return (momentCardRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
  }

  private removeSingleMoment(moments: MomentCard[], momentToRemove: MomentCard) {
    const indexToRemove = moments.indexOf(momentToRemove);
    this.brickLayer.elements.splice(indexToRemove, 1);
    this.brickLayer.redraw();
  }

  private updateExistingCards(newCards: MomentCard[]) {
    newCards.forEach((newCard, index) => {
      if (this.cardComponents[index]) {
        const instance = this.cardComponents[index].instance as IMasonryCardBase;
        instance.card = newCard;
        this.cardComponents[index].changeDetectorRef.detectChanges();
      }
    });
  }

  private removeBulkMoments(moments: MomentCard[]) {
    if (this.brickLayer) {
      const removeCount = this.removedItemsCount ? this.removedItemsCount : 8;
      moments.length = moments.length - removeCount;
      this.brickLayer.elements.length = this.brickLayer.elements.length - removeCount;
      this.brickLayer.redraw();
    }
  }
}
